import React, { useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import DataTable from "react-data-table-component";
import { customTableStyles } from "../../assets/CustomTableStyles";
import { get, post } from "../../services/api";
import { toast } from "react-toastify";
import Select from "react-select";

function MenuSubCategories() {
  const token = localStorage.getItem("token");

  const [triggerRerender, setTriggerRerender] = useState(false);

  const {
    register,
    formState: { errors, isSubmitting },
    control,
    handleSubmit,
    watch,
    setValue,
    trigger,
    reset,
  } = useForm({
    defaultValues: { id: 0, name: "", formCategoryId: "" },
  });

  const [categoryData, setCategoryData] = useState([]);
  const [subCategoriesData, setSubCategoriesData] = useState([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState("");

  const getCategories = async () => {
    if (!token) return;
    try {
      const response = await get("UserAuthentication/GetAllCategory", {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCategoryData(response.navCategory);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  const getSubCategoryList = async (categoryId) => {
    if (!token || !categoryId) return;
    try {
      const formdata = { formCategoryId: categoryId };
      const response = await post(
        `UserAuthentication/GetAllNavSubCategory`,
        formdata,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setSubCategoriesData(response);
    } catch (error) {
      console.error("Error fetching subcategories:", error);
    }
  };

  useEffect(() => {
    getCategories();
  }, []);

  useEffect(() => {
    if (selectedCategoryId) {
      getSubCategoryList(selectedCategoryId);
    } else {
      setSubCategoriesData([]);
    }
  }, [selectedCategoryId, triggerRerender]);

  const columns = [
    {
      name: "ID",
      selector: (row) => row.id,
      sortable: true,
      omit: false,
    },
    {
      name: "Name",
      selector: (row) => `${row.name}`,
      sortable: true,
    },
    {
      name: "Actions",
      selector: (row) => (
        <>
          <div className="d-flex gap-1">
            <button
              type="button"
              className="btn p-0"
              onClick={() => handleEditClick(row.id)}
            >
              <i className="fas fa-pencil"></i>
            </button>
            <button
              type="button"
              className="btn p-0"
              onClick={() => handleDeleteClick(row.id)}
            >
              <i className="fas fa-trash"></i>
            </button>
          </div>
        </>
      ),
    },
  ];

  const handleEditClick = async (rowId) => {
    var formData = { id: rowId };
    try {
      const response = await post(
        `UserAuthentication/GetNavSubCategoryById`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const selectedSubCategory = response;

      const selectedCategory = categoryData.find(
        (option) => option.id === response.formCategoryId
      );

      reset({
        id: selectedSubCategory.id,
        name: selectedSubCategory.name,
        icon: selectedSubCategory.icon,
        formCategoryId: selectedCategory
          ? { value: selectedCategory.id, label: selectedCategory.name }
          : null,
      });
    } catch (error) {
      console.error("Error fetching subcategory:", error);
    }
  };

  const handleDeleteClick = async (rowId) => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this subcategory?"
    );

    if (confirmDelete) {
      const formdata = { id: rowId };
      try {
        const response = await post(
          `UserAuthentication/DeleteNavSubCategory`,
          formdata,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        getSubCategoryList(selectedCategoryId);
      } catch (error) {
        console.error("Error deleting subcategory:", error);
      }
    }
  };

  const saveData = async (data) => {
    if (!token) {
      console.error("Unauthorized.");
      return;
    }

    const formdata = {
      ...data,
      formCategoryId: selectedCategoryId,
    };

    try {
      const response = await post(
        `UserAuthentication/AddUpdateNavSubCategory`,
        formdata,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (response.isSuccess) {
        toast.success(response.message || "Success!");
        setTriggerRerender((prev) => !prev);
        reset((formValues) => ({
          ...formValues,
          id: 0,
          icon: "",
          name: "",
        }));
      }
    } catch (error) {
      console.error("Error saving subcategory:", error);
    }
  };

  return (
    <>
      <div className="page-header d-flex mb-2">
        <h2 className="page-heading fs-7">Sub Categories</h2>
      </div>

      <form className="row g-3" onSubmit={handleSubmit(saveData)}>
        <div className="col-md-6">
          <div className="card">
            <div className="card-body">
              <div className="row g-3">
                <div className="col-md-6">
                  <label className="form-label">
                    Category<span className="text-danger fs-9">*</span>
                  </label>
                  <Controller
                    name={`formCategoryId`}
                    control={control}
                    rules={{ required: "This field is required." }}
                    render={({ field }) => (
                      <Select
                        {...field}
                        options={categoryData.map((category) => ({
                          value: category.id,
                          label: category.name,
                        }))}
                        onChange={(selectedOption, actionMeta) => {
                          if (actionMeta.action === "clear") {
                            setValue(`formCategoryId`, null);
                            setSelectedCategoryId("");
                          } else {
                            setValue(
                              `formCategoryId`,
                              selectedOption || { value: 0, label: "" }
                            );
                            setSelectedCategoryId(selectedOption?.value);
                          }
                          trigger(`formCategoryId`);
                        }}
                        className="react-select-style"
                        classNamePrefix="react-select-style-pref"
                        isClearable
                      />
                    )}
                  />
                  {errors.formCategoryId && (
                    <small className="text-danger">
                      {errors.formCategoryId.message}
                    </small>
                  )}
                </div>
                <div className="col-md-6">
                  <label className="form-label">
                    Sub Category Name<span className="text-danger fs-9">*</span>
                  </label>
                  <input
                    {...register(`name`, {
                      required: "This field is required.",
                    })}
                    type="text"
                    className="form-control"
                    placeholder=""
                  />
                  {errors.name && (
                    <small className="text-danger">{errors.name.message}</small>
                  )}
                </div>
                <div className="col-md-6">
                  <label className="form-label">
                    Icon<span className="text-danger fs-9">*</span>
                  </label>
                  <input
                    {...register(`icon`, {
                      required: "This field is required.",
                    })}
                    type="text"
                    className="form-control"
                    placeholder=""
                  />
                  {errors.icon && (
                    <small className="text-danger">{errors.icon.message}</small>
                  )}
                </div>
                <div className="col-12">
                  <button
                    className="btn btn-primary btn-sm"
                    type="submit"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Save" : "Save"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-md-6">
          <div className="card">
            <div className="card-body">
              {/* <div className="mb-3" style={{ width: "220px" }}>
                <label className="form-label">Category</label>
                <select
                  className="form-select form-control-sm"
                  value={selectedCategoryId}
                  onChange={(e) => setSelectedCategoryId(e.target.value)}
                >
                  <option value="0" key={"category-key"}>
                    Select
                  </option>
                  {categoryData.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div> */}

              <DataTable
                columns={columns}
                data={subCategoriesData}
                pagination
                dense
                customStyles={customTableStyles}
                // onRowClicked={handleRowClick}
                highlightOnHover={true}
                // pointerOnHover={true}
              />
            </div>
          </div>
        </div>
      </form>
    </>
  );
}

export default MenuSubCategories;
